'use client'

import * as React from 'react'
import { cn } from '@shared/utils'
import { HelpCircle, Sparkles, Mail, MessageSquare } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { FeatureCardProps } from '../../interfaces/FeatureCardProps'

const iconSizes = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
}

const containerSizes = {
  sm: 'h-8 w-8',
  md: 'h-12 w-12',
  lg: 'h-16 w-16',
}

const titleSizes = {
  sm: 'text-lg',
  md: 'text-xl',
  lg: 'text-2xl',
}

const variants = {
  default: 'space-y-4',
  bordered: 'space-y-4 p-6 border border-brand/10 rounded-lg',
  shadowed: 'space-y-4 p-6 shadow-md rounded-lg hover:shadow-lg transition-shadow',
}

// Map icon string keys to Lucide icon components
const iconMap: Record<string, LucideIcon> = {
  HelpCircle,
  Sparkles,
  Mail,
  MessageSquare,
  // Add additional mappings here
}

/**
 * Renders the icon container with a Lucide icon based on the given `iconName`.
 *
 * @param props.iconName - Key from the iconMap
 * @param props.iconSize - Icon size
 * @param props.iconContainerSize - Size of the icon wrapper
 */
const FeatureCardIcon = React.memo(
  ({
    iconName,
    iconSize = 'md',
    iconContainerSize = 'md',
  }: {
    iconName: string
    iconSize?: 'sm' | 'md' | 'lg'
    iconContainerSize?: 'sm' | 'md' | 'lg'
  }) => {
    const IconComponent = iconMap[iconName] || HelpCircle

    return (
      <div
        className={cn(
          containerSizes[iconContainerSize],
          'rounded-lg bg-brand/10 flex items-center justify-center'
        )}
      >
        <IconComponent className={cn(iconSizes[iconSize], 'text-brand')} />
      </div>
    )
  }
)
FeatureCardIcon.displayName = 'FeatureCardIcon'

/**
 * @group Components
 * @summary <blockquote>FeatureCard component for displaying feature information with icons.</blockquote>
 * @example
 * ```tsx
 * <FeatureCard 
 *   icon="Sparkles" 
 *   title="Amazing Feature" 
 *   description="This feature is amazing!" 
 * />
 * ```
 */
export function FeatureCard({ 
  icon,
  title,
  description,
  iconSize = 'md',
  iconContainerSize = 'md',
  variant = 'default',
  titleSize = 'md',
  className,
  ...props
}: FeatureCardProps) {
  return (
    <div className={cn(variants[variant], className)} {...props}>
      <FeatureCardIcon
        iconName={icon}
        iconSize={iconSize}
        iconContainerSize={iconContainerSize}
      />
      <h2 className={cn(titleSizes[titleSize], 'font-semibold')}>{title}</h2>
      <p className="text-brand-dark/70">{description}</p>
    </div>
  )
}
